import { useId } from "react";
import { cn } from "@/lib/utils";
import {
  CENTRO_ESTRELA,
  COR_LOGO,
  ESTRELA,
  LETRA_C1,
  LETRA_C2,
  LINHA_DA_MOLDURA,
  MOLDURA,
  VIEWBOX_LOGO,
} from "./logo-caminhos";

type Tom = "champanhe" | "taupe" | "branco";

interface LogoClaraProps {
  /** Entrada: a moldura se desenha, os dois C aparecem de cima para baixo e a estrela acende. */
  animar?: boolean;
  /** Depois da entrada, a estrela cintila e um brilho passa pelas letras (enquanto algo carrega). */
  continuo?: boolean;
  tom?: Tom;
  className?: string;
  /** Texto para leitor de tela; sem ele a logo é decorativa. */
  rotulo?: string;
}

// Perímetro da linha do meio da moldura (estádio de 267,75 x 403,4): 2 retas de 135,65 e um círculo de 267,75.
const PERIMETRO = 1113;

/**
 * A logo da Clara (monograma CC, estrela e moldura) em SVG. Animada, abre o app e as telas que carregam; parada,
 * vai no topo do app. As animações ficam em src/styles.css (.logo-clara) e somem com movimento reduzido.
 */
const LogoClara = ({ animar = false, continuo = false, tom = "champanhe", className, rotulo }: LogoClaraProps) => {
  const id = useId().replace(/[^a-zA-Z0-9]/g, "");
  const cor = tom === "branco" ? "#ffffff" : tom === "taupe" ? COR_LOGO : `url(#cor-${id})`;

  return (
    <svg
      viewBox={VIEWBOX_LOGO}
      className={cn("logo-clara", animar && "logo-clara-entrar", continuo && "logo-clara-continuo", className)}
      role={rotulo ? "img" : undefined}
      aria-label={rotulo}
      aria-hidden={rotulo ? undefined : true}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        {/* Champanhe: o mesmo tom do ícone metálico, com luz em diagonal. */}
        <linearGradient id={`cor-${id}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#c9b99c" />
          <stop offset="0.42" stopColor="#8e8779" />
          <stop offset="0.7" stopColor="#bfae90" />
          <stop offset="1" stopColor="#857b6a" />
        </linearGradient>
        <linearGradient id={`brilho-${id}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#fff" stopOpacity="0" />
          <stop offset="0.5" stopColor="#fff" stopOpacity="0.65" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        {/* A moldura aparece por baixo de um traço que se desenha ao longo dela. */}
        <mask id={`moldura-${id}`} maskUnits="userSpaceOnUse" x="90" y="30" width="320" height="440">
          <path
            className="logo-clara-traco"
            d={LINHA_DA_MOLDURA}
            fill="none"
            stroke="#fff"
            strokeWidth="14"
            strokeLinecap="round"
            strokeDasharray={PERIMETRO}
            strokeDashoffset={0}
          />
        </mask>
        {/* Cada C aparece de cima para baixo, como uma cortina que desce. */}
        <mask id={`c1-${id}`} maskUnits="userSpaceOnUse" x="90" y="30" width="320" height="440">
          <rect className="logo-clara-cortina logo-clara-cortina-1" x="150" y="112" width="160" height="165" fill="#fff" />
        </mask>
        <mask id={`c2-${id}`} maskUnits="userSpaceOnUse" x="90" y="30" width="320" height="440">
          <rect className="logo-clara-cortina logo-clara-cortina-2" x="190" y="228" width="155" height="165" fill="#fff" />
        </mask>
        <clipPath id={`letras-${id}`}>
          <path d={LETRA_C1} />
          <path d={LETRA_C2} />
          <path d={ESTRELA} />
        </clipPath>
      </defs>

      <path d={MOLDURA} fill={cor} fillRule="evenodd" mask={animar ? `url(#moldura-${id})` : undefined} />
      <path d={LETRA_C1} fill={cor} fillRule="evenodd" mask={animar ? `url(#c1-${id})` : undefined} />
      <path d={LETRA_C2} fill={cor} fillRule="evenodd" mask={animar ? `url(#c2-${id})` : undefined} />
      <g
        className="logo-clara-estrela"
        style={{ transformOrigin: `${CENTRO_ESTRELA.x}px ${CENTRO_ESTRELA.y}px` }}
      >
        <path d={ESTRELA} fill={cor} fillRule="evenodd" />
      </g>
      {continuo && tom !== "branco" && (
        <g clipPath={`url(#letras-${id})`}>
          <rect className="logo-clara-brilho" x="40" y="100" width="90" height="320" fill={`url(#brilho-${id})`} />
        </g>
      )}
    </svg>
  );
};

export default LogoClara;
